/**
 * useVeil - the veil the sync layer applies at its boundaries (plan map-display, G1).
 *
 * A hidden figure may act, and when the GM keeps it hidden the act goes out
 * VEILED (ADR 2026-10-07): what a player could read of it names "Unknown N",
 * never the figure. The veil is made from the GM's hidden figures, the
 * working roster's names and the GM's actor (engine `makeVeil`), and handed
 * to the sync layer as a ref-like object whose `current` is the veil of the
 * moment it is READ: the figures come from the store's own ref, which a verb
 * changes at once, so a reveal followed by its act in the same tick is not
 * veiled by the render before. Rebuilt only when one of the three changes.
 *
 * @param {{ current: Map }} figuresRef - the hidden figures' store (useHiddenFigures `figuresRef`)
 * @param {object[]} roster - the working roster
 * @param {string|null} actorId - who this screen acts as
 * @returns {{ readonly current: import('../engine/roster/hidden').Veil }}
 */
import { useMemo } from 'react';
import { makeVeil } from '../engine/roster/hidden';
import useMirror from './useMirror';

export default function useVeil(figuresRef, roster, actorId) {
  const rosterRef = useMirror(roster);
  const actorRef = useMirror(actorId);
  return useMemo(() => {
    let made = { figures: null, roster: null, actorId: undefined, veil: null };
    return {
      get current() {
        const figures = figuresRef.current;
        const now = { figures, roster: rosterRef.current, actorId: actorRef.current };
        if (now.figures !== made.figures || now.roster !== made.roster || now.actorId !== made.actorId) {
          made = { ...now, veil: makeVeil([...(figures?.values() ?? [])], now.roster ?? [], now.actorId) };
        }
        return made.veil;
      },
    };
  }, [figuresRef, rosterRef, actorRef]);
}
